import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Zap, RefreshCw, Cpu, Cloud, GitMerge, CheckSquare, Square } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

const BlendStudio = () => {
  const [availableModels, setAvailableModels] = useState([]);
  const [selectedModels, setSelectedModels] = useState([]);
  const [prompt, setPrompt] = useState('');
  const [mode, setMode] = useState('blend'); // 'blend' or 'compare'
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchModels();
  }, []);

  const fetchModels = async () => {
    try {
      const res = await fetch(`${BACKEND}/api/ai/models/available`);
      const data = await res.json();
      setAvailableModels(data.models || []);
      // Pre-select first 2 available models
      const avail = (data.models || []).filter(m => m.available);
      setSelectedModels(avail.slice(0, 2).map(m => m.id));
    } catch {
      // fallback static list if backend not reachable
      setAvailableModels([
        { id: 'qwen2.5:7b',     label: 'Qwen 2.5 7B',    provider: 'Ollama (local)', type: 'local', available: true },
        { id: 'llama3.1',       label: 'Llama 3.1 8B',   provider: 'Ollama (local)', type: 'local', available: true },
        { id: 'mistral',        label: 'Mistral 7B',      provider: 'Ollama (local)', type: 'local', available: true },
        { id: 'deepseek-coder', label: 'DeepSeek Coder',  provider: 'Ollama (local)', type: 'local', available: true },
        { id: 'groq',           label: 'Llama 3.3 70B',   provider: 'Groq (cloud)',   type: 'cloud', available: false },
      ]);
      setSelectedModels(['qwen2.5:7b', 'llama3.1']);
    }
  };

  const toggleModel = (id) => {
    setSelectedModels(prev => {
      if (prev.includes(id)) {
        return prev.filter(m => m !== id);
      }
      if (prev.length >= 4) {
        toast({ title: 'Max 4 models', description: 'Deselect one before adding another.', variant: 'destructive' });
        return prev;
      }
      return [...prev, id];
    });
  };

  const handleBlend = async () => {
    if (!prompt.trim()) {
      toast({ title: 'Enter a prompt', description: 'Type something for the models to answer.', variant: 'destructive' });
      return;
    }
    if (selectedModels.length < 2) {
      toast({ title: 'Select at least 2 models', variant: 'destructive' });
      return;
    }
    setLoading(true);
    setResults(null);
    try {
      const res = await fetch(`${BACKEND}/api/ai/blend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: prompt.trim(), models: selectedModels, mode }),
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setResults(data);
    } catch (e) {
      toast({ title: 'Blend failed', description: e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const modelColor = (type) => type === 'cloud'
    ? 'bg-purple-500/20 text-purple-300 border-purple-500/30'
    : 'bg-blue-500/20 text-blue-300 border-blue-500/30';

  return (
    <div className="min-h-screen py-8 px-6">
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
            <GitMerge className="w-8 h-8 text-blue-400" />
            AI Blend Studio
          </h1>
          <p className="text-gray-400">
            Run your prompt through multiple AI models simultaneously — then blend them into one refined answer.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* Left: Model Picker */}
          <div className="lg:col-span-1">
            <Card className="bg-gray-800/50 border-gray-700 p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-white font-semibold">Select Models</h2>
                <span className="text-xs text-gray-400">{selectedModels.length}/4 selected</span>
              </div>

              <div className="space-y-3">
                {availableModels.map(model => {
                  const selected = selectedModels.includes(model.id);
                  const disabled = !model.available;
                  return (
                    <button
                      key={model.id}
                      onClick={() => !disabled && toggleModel(model.id)}
                      disabled={disabled}
                      className={`w-full text-left rounded-lg border p-3 transition-all duration-200
                        ${disabled ? 'opacity-40 cursor-not-allowed border-gray-700 bg-gray-900/30' :
                          selected ? 'border-blue-500 bg-blue-500/10 cursor-pointer' :
                          'border-gray-700 bg-gray-900/30 hover:border-gray-500 cursor-pointer'}`}
                    >
                      <div className="flex items-center gap-3">
                        <span className={`shrink-0 ${selected ? 'text-blue-400' : 'text-gray-500'}`}>
                          {selected ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-medium text-white truncate">{model.label}</span>
                            {model.type === 'cloud'
                              ? <Cloud className="w-3 h-3 text-purple-400 shrink-0" />
                              : <Cpu className="w-3 h-3 text-blue-400 shrink-0" />}
                          </div>
                          <span className="text-xs text-gray-400">{model.provider}</span>
                          {disabled && <span className="block text-xs text-yellow-500 mt-0.5">API key required</span>}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="mt-5 border-t border-gray-700 pt-4">
                <p className="text-xs text-gray-500 mb-3">Output mode</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setMode('blend')}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors
                      ${mode === 'blend' ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
                  >
                    Blend
                  </button>
                  <button
                    onClick={() => setMode('compare')}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors
                      ${mode === 'compare' ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
                  >
                    Compare
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {mode === 'blend'
                    ? 'Synthesizes a single refined answer from all models.'
                    : 'Shows each model\'s answer side by side — no synthesis.'}
                </p>
              </div>
            </Card>
          </div>

          {/* Right: Prompt + Results */}
          <div className="lg:col-span-2 space-y-5">

            {/* Prompt input */}
            <Card className="bg-gray-800/50 border-gray-700 p-5">
              <h2 className="text-white font-semibold mb-3">Your Prompt</h2>
              <Textarea
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
                placeholder="Ask anything — 'Write a tagline for a bakery', 'Explain quantum computing simply', 'Give me 5 email subject lines for a sale'..."
                className="bg-gray-900 border-gray-600 text-white min-h-[120px] resize-none mb-4"
              />
              <Button
                onClick={handleBlend}
                disabled={loading || selectedModels.length < 2}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 py-3"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Running {selectedModels.length} models in parallel...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Zap className="w-4 h-4" />
                    {mode === 'blend' ? `Blend ${selectedModels.length} Models` : `Compare ${selectedModels.length} Models`}
                  </span>
                )}
              </Button>
            </Card>

            {/* Results */}
            {results && (
              <>
                {/* Individual model outputs */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {results.results.map(r => (
                    <Card key={r.model} className={`border p-4 ${r.error ? 'border-red-700 bg-red-900/10' : 'border-gray-700 bg-gray-800/50'}`}>
                      <div className="flex items-center gap-2 mb-3">
                        {BLEND_MODELS_META[r.model]?.type === 'cloud'
                          ? <Cloud className="w-4 h-4 text-purple-400" />
                          : <Cpu className="w-4 h-4 text-blue-400" />}
                        <span className="font-medium text-white text-sm">{r.label}</span>
                        {r.error
                          ? <Badge className="ml-auto bg-red-500/20 text-red-400 text-xs">Error</Badge>
                          : <Badge className="ml-auto bg-green-500/20 text-green-400 text-xs">Done</Badge>}
                      </div>
                      {r.error
                        ? <p className="text-red-400 text-sm">{r.error}</p>
                        : <p className="text-gray-300 text-sm whitespace-pre-wrap leading-relaxed">{r.output}</p>}
                    </Card>
                  ))}
                </div>

                {/* Blended result */}
                {results.mode === 'blend' && results.blended && (
                  <Card className="border border-blue-500/40 bg-blue-900/10 p-5">
                    <div className="flex items-center gap-2 mb-4">
                      <GitMerge className="w-5 h-5 text-blue-400" />
                      <h3 className="text-white font-semibold">Blended Answer</h3>
                      <Badge className="ml-auto bg-blue-500/20 text-blue-300 text-xs">
                        Synthesized from {results.models_used} models
                      </Badge>
                    </div>
                    <p className="text-gray-200 whitespace-pre-wrap leading-relaxed">{results.blended}</p>
                  </Card>
                )}
              </>
            )}

            {/* Empty state */}
            {!results && !loading && (
              <Card className="border border-gray-700 bg-gray-800/20 p-10 text-center">
                <GitMerge className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400 text-sm">
                  Select 2–4 models, type a prompt, and hit Blend.<br />
                  Each model answers in parallel — Mistral synthesizes the best of all.
                </p>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Used in results rendering for icon logic
const BLEND_MODELS_META = {
  'qwen2.5:7b':     { type: 'local' },
  'llama3.1':       { type: 'local' },
  'mistral':        { type: 'local' },
  'deepseek-coder': { type: 'local' },
  'groq':           { type: 'cloud' },
};

export default BlendStudio;
